import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  deleteExistingTeacher,
  fetchTeachers,
  selectNoTeachers,
  selectTeacherActionLoading,
  selectTeacherLoading,
  selectTeachers,
  selectTotalItems,
} from "@/store/slices/teacherSlice";
import { AppDispatch } from "@store";
import { ChevronLeft, Eye, SquarePen, Trash2, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

// Using the TeacherModel from types
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import SelectWithRadio from "@/components/design-system/SelectWithRadio";
import { getAssignResourceDropdownV2Service, type AssignResourceType } from "@/service/base.service";
import { resolveSingleSchoolId } from "@/modules/User-Management/utils/schoolContext";
import { isAllSchoolsSelected, selectSchoolId, selectUserRole } from "@/store/slices/authSlice";
import { TeacherModel } from "@/types/teacher.model";
import {
  capitalizeFirstLetter,
  hasMenuPermission,
  hasRolePermissions,
  ROLE_PERMISSIONS,
} from "@/utils/constants";
import Breadcrumbs from "@/components/design-system/breadcrumbs";

// Extended interface for UI display purposes
interface TeacherDisplay extends TeacherModel {
  sno?: number;
}

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function TeacherListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const [isAssignStudentModalOpen, setIsAssignStudentModalOpen] =
    useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    null
  );
  const [assignType, setAssignType] = useState<AssignResourceType | "">("");
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [resourceOptions, setResourceOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [resourceLoading, setResourceLoading] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const teachers = useSelector(selectTeachers);
  const actionLoading = useSelector(selectTeacherActionLoading);
  const loading = useSelector(selectTeacherLoading);
  const totalItems = useSelector(selectTotalItems);
  const noTeachers = useSelector(selectNoTeachers);

  const userRole = useSelector(selectUserRole);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const schoolId = searchParams.get("schoolId");
  const schoolIdFromUser = useSelector(selectSchoolId);
  const isAllSchools = useSelector(isAllSchoolsSelected);


  // Handle delete teacher
  const handleDeleteTeacher = () => {
    if (teacherToDelete) {
      dispatch(deleteExistingTeacher(teacherToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setTeacherToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete Teacher:", error);
          // Modal stays open on error
        });
    }
  };

  const handleAssignStudent = (teacher: TeacherDisplay) => {
    setSelectedTeacherId(teacher?.id);
    setAssignType("");
    setSelectedResourceId(null);
    setResourceOptions([]);
    setIsAssignStudentModalOpen(true);
  };

  const fetchResourceOptions = async (type: AssignResourceType) => {
    setResourceLoading(true);
    try {
      const scopedSchoolId = resolveSingleSchoolId(schoolId, schoolIdFromUser);
      const { data } = await getAssignResourceDropdownV2Service(
        type,
        scopedSchoolId ? { schoolId: scopedSchoolId } : undefined
      );
      const items = (data.data ?? []).map((item) => {
        if (type === "course" && "grade" in item) {
          return { id: item.id, label: item.grade };
        }
        if ("name" in item) {
          return { id: item.id, label: item.name };
        }
        return { id: item.id, label: item.id };
      });
      setResourceOptions(items);
    } catch (error) {
      console.error("Error fetching assign options:", error);
      setResourceOptions([]);
    } finally {
      setResourceLoading(false);
    }
  };

  const handleAssignTypeChange = (type: AssignResourceType) => {
    setAssignType(type);
    setSelectedResourceId(null);
    void fetchResourceOptions(type);
  };

  const resetAssignModal = () => {
    setIsAssignStudentModalOpen(false);
    setAssignType("");
    setSelectedResourceId(null);
    setResourceOptions([]);
  };

  const assignResourceLabel =
    assignType === "class" ? "Assign Class" : "Assign Grade";
  const assignResourcePlaceholder = resourceLoading
    ? "Loading…"
    : assignType === "class"
      ? "Select a Class"
      : assignType === "course"
        ? "Select a Grade"
        : "Select";

  // Handle export functionality
  // const handleExport = async (type: "pdf" | "xl" | "csv") => {
  //   try {
  //     const response = await downloadTeacherService(type, {
  //       page: currentPage,
  //       pageSize: ITEMS_PER_PAGE,
  //       search: searchValue,
  //       fromDate: fromDate,
  //       toDate: toDate,
  //     });

  //     // Create a blob from the response data
  //     const blob = new Blob([response.data as unknown as BlobPart], {
  //       type:
  //         type === "pdf"
  //           ? "application/pdf"
  //           : type === "xl"
  //           ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  //           : "text/csv",
  //     });

  //     // Create a URL for the blob
  //     const url = window.URL.createObjectURL(blob);

  //     // Create a link element and trigger download
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = `teachers_export.${
  //       type === "xl" ? "xlsx" : type === "pdf" ? "pdf" : "csv"
  //     }`;
  //     document.body.appendChild(a);
  //     a.click();

  //     // Clean up
  //     window.URL.revokeObjectURL(url);
  //     document.body.removeChild(a);

  //     successToast("Export successful", "");
  //   } catch (error) {
  //     console.error("Export failed:", error);
  //     errorToast("Export failed", "");
  //   }
  // };

  // const exportOptions: ExportOption[] = [
  //   {
  //     label: "PDF",
  //     icon: FileText,
  //     action: () => handleExport("pdf"),
  //     iconColor: "text-[#5C88C5]",
  //   },
  //   {
  //     label: "Excel",
  //     icon: FileSpreadsheet,
  //     action: () => handleExport("xl"),
  //     iconColor: "text-[#5C88C5]",
  //   },
  //   {
  //     label: "CSV",
  //     icon: Sheet,
  //     action: () => handleExport("csv"),
  //     iconColor: "text-[#5C88C5]",
  //   },
  // ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map frontend column keys to backend API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      name: "NME",
      grade: "GRDE",
      students: "STDS",
      email: "EML",
      contact_number: "CNUM",
      is_active: "ACT",
      school_name: "SCH",
    };
    return sortFieldMap[key] || key;
  };

  // Fetch teachers on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Prepare sorting parameters if sort config exists
    const sortParams = sortConfig
      ? {
        sortBy: getSortField(sortConfig.key),
      }
      : {};

    dispatch(
      fetchTeachers({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        fromDate: fromDate,
        toDate: toDate,
        schoolId: schoolId || schoolIdFromUser || undefined,
        ...sortParams,
      })
    );
  }, [dispatch, currentPage, searchValue, fromDate, toDate, sortConfig, schoolIdFromUser]);


  const schoolColumn: any = {
    key: "school_name",
    title: "School Name",
    dataIndex: "school_name",
    minWidth: 250,
    maxWidth: 250,
    sortable: true,
    sortKey: "school_name",
    render: (_: any, record: any) => (
      <div
        className="text-[#1F1F1F] text-[16px] font-medium"
        style={{
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {record?.school_name || "N/A"}
      </div>
    ),
  };

  const columns: TableColumn<TeacherDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "name",
      title: "Teacher Name",
      dataIndex: "name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "gender",
      title: "Gender",
      minWidth: 148,
      maxWidth: 148,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {capitalizeFirstLetter(record.gender) || "N/A"}
        </div>
      ),
    },
    ...(isAllSchools ? [schoolColumn] : []),
    {
      key: "grade",
      title: "Grade Assigned",
      minWidth: 180,
      maxWidth: 180,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {Array.isArray(record.grade)
            ? record.grade.map((grade) => grade.grade).join(", ")
            : record.grade || "N/A"}
        </div>
      ),
    },
    {
      key: "students",
      title: "No of Student Assigned",
      minWidth: 273,
      maxWidth: 273,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.students || "0"}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.email || "N/A"}
        </div>
      ),
    },
    {
      key: "contact_number",
      title: "Contact Number",
      minWidth: 278,
      maxWidth: 278,
      align: "left",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.contact_number || "N/A"}
        </div>
      ),
    },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_TEACHER,
      ROLE_PERMISSIONS.ACTION_EDIT_TEACHER,
      ROLE_PERMISSIONS.ACTION_DELETE_TEACHER,
      ROLE_PERMISSIONS.ACTION_ASSIGN_STUDENTS,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 204,
      maxWidth: 204,
      align: "left",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_TEACHER,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() =>
                  navigate(schoolId ? `/user-management/teacher/view-teacher/${record.id}?schoolId=${schoolId}` : `/user-management/teacher/view-teacher/${record.id}`)
                }
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_EDIT_TEACHER,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit"
                onClick={() =>
                  navigate(schoolId ? `/user-management/teacher/edit-teacher/${record.id}?schoolId=${schoolId}` : `/user-management/teacher/edit-teacher/${record.id}`)
                }
              >
                <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_DELETE_TEACHER,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Delete"
                onClick={() => {
                  setDeleteModalOpen(true);
                  setTeacherToDelete(record.id);
                }}
              >
                <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}

          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_ASSIGN_STUDENTS,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Assign Student"
                onClick={() => handleAssignStudent(record)}
              >
                <UserPlus className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
        </div>
      ),
    });
  }

  const backURL: any = sessionStorage.getItem("schoolBackURL") || '/school-management';

  if (noTeachers) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        {schoolId && <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem("schoolBackURL");
            navigate(backURL);
          }}
          className={`h-[40px] w-[40px] bg-[#DEEAF7] rounded-[8px] flex items-center justify-center mt-6`}
        >
          <ChevronLeft className="h-[24px] w-[24px] text-[#666666]" />
        </button>}
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No teachers added.
              </p>

              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Create teacher profiles and assign them to courses and students.
              </p>

              {hasMenuPermission(
                ROLE_PERMISSIONS.BUTTON_ADD_TEACHER,
                userRole?.permissions as string[]
              ) && (
                  <Button
                    className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                    onClick={() => navigate(schoolId ? `/user-management/teacher/add-teacher?schoolId=${schoolId}` : "/user-management/teacher/add-teacher")}
                  >
                    Add Teacher
                  </Button>
                )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden  h-full flex flex-1 flex-col">

      <ListHeader
        title={<div className="flex items-center justify-center">
          {schoolId && <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem("schoolBackURL");
              navigate(backURL);
            }}
            className={`h-[40px] w-[40px] bg-[#DEEAF7] rounded-[8px] flex items-center justify-center mr-2`}
          >
            <ChevronLeft className="h-[24px] w-[24px] text-[#666666]" />
          </button>}
          Teacher List
        </div>}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        rightAction={
          <>
            {/* Export Button */}
            {/* <ExportDropdown options={exportOptions} /> */}

            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_ADD_TEACHER,
              userRole?.permissions as string[]
            ) && (
                <Button
                  variant="primary"
                  size="md"
                  className="w-[161px] h-[48px] bg-[#5C88C5] text-white"
                  onClick={() => navigate(schoolId ? `/user-management/teacher/add-teacher?schoolId=${schoolId}` : "/user-management/teacher/add-teacher")}
                >
                  Add Teacher
                </Button>
              )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          {/* {loading ? (
            <div className="flex items-center justify-center h-full w-full">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading teachers...</p>
              </div>
            </div>
          ) : ( */}
          <Table
            columns={columns}
            data={teachers}
            rowKey="id"
            showHeader={true}
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          {/* )} */}
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Teachers"
      />

      {/* Delete Student Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Teacher"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete Teacher ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteTeacher()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>

      {/* Assign Student Modal */}
      <Modal
        isOpen={isAssignStudentModalOpen}
        onClose={resetAssignModal}
        title="Set Grade"
        width="728px"
        contentClassName="p-0"
      >
        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
          <SelectWithRadio
            label="Select Type"
            showAbove={false}
            options={[
              { label: "Course", value: "course" },
              { label: "Class", value: "class" },
            ]}
            onChange={(value) => handleAssignTypeChange(value as AssignResourceType)}
            value={assignType}
            placeholder="Select Type"
          />
          <SelectWithRadio
            label={assignResourceLabel}
            showAbove={false}
            options={resourceOptions.map((option) => ({
              label: option.label,
              value: option.id,
            }))}
            onChange={(value) => setSelectedResourceId(value)}
            value={selectedResourceId || ""}
            disabled={!assignType || resourceLoading}
            placeholder={assignResourcePlaceholder}
          />
        </div>
        <div className="flex justify-end gap-4 border-t border-gray-200 p-4">
          <Button
            variant="light"
            size="md"
            className="h-[48px] w-[160px] border border-[#5C88C5] bg-[#fff] text-dark"
            onClick={resetAssignModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="h-[48px] w-[160px] bg-[#5C88C5] text-white"
            disabled={!assignType || !selectedResourceId}
            onClick={() => {
              const selectedItem = resourceOptions.find(
                (option) => option.id === selectedResourceId
              );
              const baseParams = new URLSearchParams({
                teacherId: selectedTeacherId || "",
              });
              const navSchoolId = resolveSingleSchoolId(schoolId, schoolIdFromUser);
              if (navSchoolId) {
                baseParams.set("schoolId", navSchoolId);
              }

              if (assignType === "class") {
                baseParams.set("assignType", "class");
                baseParams.set("classId", selectedResourceId || "");
                baseParams.set(
                  "className",
                  selectedItem?.label || ""
                );
              } else {
                baseParams.set("assignType", "course");
                baseParams.set("grade", selectedResourceId || "");
                baseParams.set("gradeName", selectedItem?.label || "");
              }

              navigate(
                `/user-management/teacher/assign-student?${baseParams.toString()}`
              );
              resetAssignModal();
            }}
          >
            Continue
          </Button>
        </div>
      </Modal>
    </div>
  );
}
